import { useState } from "react";
import { useAppContext } from "../app-context/provider";
import { ProductVariant } from "../../use-cases/contracts/ProductVariant";
import { useCart } from "~/components/cart/useCart";
import { useKlaviyo } from "./aplia/Klaviyo";

export type VariantPack = VariantPackItem[];
export type VariantPackItem = {
  variant: ProductVariant;
  quantity: number;
  key?: string;
};

export const AddToCartBtnSimple: React.FC<{
  pack: VariantPack;
  label?: string;
}> = ({ pack, label = "addToCart" }) => {
  const [showTada, setShowTada] = useState(false);
  const [showOhNo, setShowOhNo] = useState(false);
  const { dispatch: contextDispatch, _t } = useAppContext();
  const { getKlaviyoUser } = useKlaviyo();
  const cart = useCart();

  const handleClick = async () => {
    const user = getKlaviyoUser();

    setShowTada(true);
    setShowOhNo(false);

    const results = await Promise.all(
      pack.map((p) => cart.add(p.variant.sku, p.quantity, user?.email)),
    );

    const firstFailure = results.find((r) => !r.success) as
      | { success: false; reason: string }
      | undefined;

    if (firstFailure) {
      setShowTada(false);
      setShowOhNo(true);
      if (firstFailure.reason === "OTC") {
        contextDispatch.addOtcRejectItems(pack.map((p) => p.variant));
      } else {
        contextDispatch.addOutOfStockRejectItems(pack.map((p) => p.variant));
      }
    } else {
      contextDispatch.addItemsToCart(pack.map((p) => p.variant));
    }

    setTimeout(() => {
      setShowTada(false);
      setShowOhNo(false);
    }, 1500);
  };

  const defaultStock = pack.reduce((memo: number, p: VariantPackItem) => {
    const locationStock = Object.values(p.variant?.stockLocations ?? {}).reduce(
      (sum, loc) => sum + (loc?.stock ?? 0),
      0,
    );
    if (p.variant?.outofstock?.content?.value === true) return memo;
    return memo + (Object.keys(p.variant?.stockLocations ?? {}).length > 0 ? locationStock : 1);
  }, 0);

  return (
    <div className="mt-4">
      <button
        data-testid="add-to-cart-button"
        className="button relative overflow-hidden"
        onClick={handleClick}
        disabled={defaultStock < 1}
      >
        {/* Default label */}
        <span
          className={`transition-all duration-200 flex items-center justify-center w-full ${
            showTada || showOhNo ? "scale-0 opacity-0" : "scale-100 opacity-100"
          }`}
        >
          {_t(label)}
        </span>

        {/* Success: checkmark */}
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
            showTada ? "scale-100 opacity-100" : "scale-0 opacity-0"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>

        {/* Error: X */}
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
            showOhNo ? "scale-100 opacity-100" : "scale-0 opacity-0"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
      </button>
    </div>
  );
};
